import { HouseholdRelationship } from '@access-management/access-management';

/**
 * Mirrors the engine's InvitationStatus. `PendingPurchase` is where onboarding leaves an invitation
 * until the seat is paid for, and it reaches the wire under that name: the engine renamed it from
 * `Draft` in the AddSubscriptionIdAndRenamePendingPurchase migration.
 */
export enum InvitationStatus {
  PendingPurchase = 'PendingPurchase',
  Pending = 'Pending',
  Accepted = 'Accepted',
  Declined = 'Declined',
  Expired = 'Expired',
  Cancelled = 'Cancelled'
}

export enum Relationship {
  Child = 'Child',
  Spouse = 'Spouse',
  Parent = 'Parent',
  Sibling = 'Sibling',
  Other = 'Other'
}

export interface InvitationResponseV1 {
  invitationId: string;
  groupId: string;
  groupName?: string;
  inviterUserId: string;
  inviterName?: string;
  inviteeEmail: string;
  inviteeUserId?: string;
  status: InvitationStatus;
  householdRelationship: HouseholdRelationship;
  shareInviterInformation: boolean;
  isPendingPurchase: boolean;
  expiresOn?: string;
  acceptedOn?: string;
  declinedOn?: string;
  cancelledOn?: string;
  guardianConsentAcceptedOn?: string;
  tosConsentAcceptedOn?: string;
  privacyConsentAcceptedOn?: string;
  dependentResponsibilityConsentAcceptedOn?: string;
  createdOn: string;
  relationship?: Relationship;
  hasSmartDevice?: boolean;
  inviteeFirstName?: string;
  inviteeLastName?: string;
  inviteeDateOfBirth?: string;
  invitationToken: string;
  /**
   * Set when staging returned an invitation that already existed instead of creating one. Staging is
   * idempotent, so this arrives as a success, and reporting it as a fresh send tells staff an email went
   * out that did not.
   */
  alreadyPending?: boolean;
}

export interface InviteeInfoV1 {
  email: string;
  householdRelationship: HouseholdRelationship;
  roleName: string;
  shareInviterInformation: boolean;
  relationship?: Relationship;
  hasSmartDevice?: boolean;
  inviteeFirstName?: string;
  inviteeLastName?: string;
}

export interface InviteUsersToGroupRequestV1 {
  inviterUserId: string;
  invitees: InviteeInfoV1[];
}

/**
 * One invitee the batch refused. Reason is the rule's own message: an email that already has a Youtopia
 * account produces `AccountExists`, which is the most common refusal from this screen.
 */
export interface InvitationErrorV1 {
  email: string;
  reason: string;
  alreadyPending: boolean;
}

/**
 * The invite endpoint answers 200 whenever the batch itself was well formed, and reports each invitee's
 * fate inside the body. A caller that reads only the status code shows success for a refused invitee.
 */
export interface InvitationBatchResponseV1 {
  successes: InvitationResponseV1[];
  errors: InvitationErrorV1[];
}

export interface InvitationStatusCountsV1 {
  pendingPurchase: number;
  pending: number;
  accepted: number;
  declined: number;
  expired: number;
  cancelled: number;
}

export interface InvitationWindowCountsV1 {
  created: number;
  accepted: number;
  declined: number;
  expired: number;
}

export interface InvitationActivityWindowsV1 {
  last7Days: InvitationWindowCountsV1;
  last30Days: InvitationWindowCountsV1;
}

export interface InviterStatsV1 {
  inviterUserId: string;
  inviterName?: string;
  inviterEmail?: string;
  totalInvitationsSent: number;
  acceptedCount: number;
  pendingCount: number;
  expiredCount: number;
  declinedCount: number;
  cancelledCount: number;
  acceptanceRatePercent?: number;
}

export interface GroupInvitationStatsV1 {
  groupId: string;
  groupName?: string;
  totalInvitations: number;
  pendingCount: number;
  acceptedCount: number;
  expiredCount: number;
  declinedCount: number;
  cancelledCount: number;
  draftCount: number;
  latestInvitationOn?: string;
}

export interface InvitationsSummaryV1 {
  generatedAt: string;
  totalInvitations: number;
  byStatus: InvitationStatusCountsV1;
  pendingButTimeExpiredCount: number;
  recentActivity: InvitationActivityWindowsV1;
  topInviters: InviterStatsV1[];
  byGroup: GroupInvitationStatsV1[];
}
